import { Button ,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@roberttamas/ui";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Loader2 } from "@/components/icons";

interface SettingRowProps {
  title: string;
  description?: string;
  /** Read-only value display. */
  value: ReactNode;
  /** Right-aligned affordance (usually an Edit button). Omit for read-only. */
  action?: ReactNode;
}

/**
 * A single read-only settings row. Editing happens in a modal (see
 * {@link EditDialog}), never inline — see docs/ui-guidelines.md.
 */
export function SettingRow({ title, description, value, action }: SettingRowProps) {
  return (
    <div className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div className="min-w-0 space-y-1">
        <h3 className="text-sm font-medium">{title}</h3>
        {description && <p className="text-xs text-muted-foreground">{description}</p>}
        <div className="pt-1 text-sm">{value}</div>
      </div>
      {action && (
        <div className="w-full shrink-0 sm:-mt-1 sm:w-auto [&>button]:w-full sm:[&>button]:w-auto">
          {action}
        </div>
      )}
    </div>
  );
}

interface EditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  onSave: () => void;
  saving?: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  /** Optional wider dialog for content-heavy editors (e.g. text overrides). */
  contentClassName?: string;
}

/**
 * Generic editing dialog shell: titled header, stacked fields, and a
 * Cancel/Save footer. Save is disabled while pristine/invalid and shows a
 * spinner while the mutation runs. The dialog can't be dismissed mid-save.
 */
export function EditDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  onSave,
  saving = false,
  saveDisabled = false,
  saveLabel,
  contentClassName,
}: EditDialogProps) {
  const { t } = useTranslation();
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className={contentClassName}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="ui-scrollbar -mx-2 max-h-[60vh] space-y-4 overflow-y-auto px-2 py-1 sm:max-h-[72vh]">
          {children}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            {t("common.cancel")}
          </Button>
          <Button type="button" onClick={onSave} disabled={saving || saveDisabled}>
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> {t("common.saving")}
              </>
            ) : (
              saveLabel ?? t("common.save")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
